import { hasCurveProfileParams } from '../../../../utils/feature/profileSplinePredicate';
import {
    buildTopologyEdgeRef,
    buildTopologyFaceRef,
    buildTopologySubEntityId,
    buildTopologyVertexRef,
} from '../../../../topology/TopologyReferenceBuilders';
import {
    sampleProfileSplineCurveSegment,
    type ProfileCurveSampleContext,
} from '../profile/sampling/profile-curve-sampler';
import { ShapeUtils, Vector2 } from 'three';

type Point3 = [number, number, number];

/**
 * 曲线旋转显示网格精度。
 *
 * 交互预览用于命令参数变化期间的即时反馈；正式结果用于持久化特征的最终显示网格。
 */
export type CurveRevolveVisualMeshDetail = 'interactive-preview' | 'final-result';

interface RevolvePreviewLoop {
    /** 已按曲线语义采样后的闭合轮廓点。 */
    points: Point3[];
    /** 该轮廓对应的底层曲线类型，用于生成可视拓扑边线。 */
    curveKind: TopologyCurveKind;
}

interface RevolvePreviewRegion {
    /** 参与旋转预览的所有边界环。 */
    loops: RevolvePreviewLoop[];
    /** 区域法向，用于非完整旋转端面的三角化朝向。 */
    normal: Point3;
    /** 区域局部 X 轴，用于把端面轮廓投影到二维平面。 */
    xAxis: Point3;
    /** 区域局部 Y 轴，用于把端面轮廓投影到二维平面。 */
    yAxis: Point3;
}

interface CurveRevolveVisualMeshOptions {
    /** 正式结果体所属特征 ID；普通预览态没有真实特征 ID 时保持为空。 */
    featureId?: string | null;
    /** profile 曲线采样上下文；插值样条需要通过 Puppy CAD 内核 恢复和草图一致的曲线。 */
    curveSampleContext?: ProfileCurveSampleContext;
    /** 当前直接网格的显示用途；刀具用途只改变准入语义，不改变几何生成算法。 */
    purpose?: 'feature-result-preview' | 'cut-tool-preview';
    /** 网格精度；交互预览降低分段数，正式结果保持高精度。 */
    meshDetail?: CurveRevolveVisualMeshDetail;
}

const FULL_TURN_RADIANS = Math.PI * 2;
const POINT_EPSILON = 1e-9;
const FINAL_PROFILE_CURVE_SEGMENTS = 360;
const FINAL_REVOLVE_CURVE_SEGMENTS = 360;
const INTERACTIVE_PROFILE_CURVE_SEGMENTS = 96;
const INTERACTIVE_REVOLVE_CURVE_SEGMENTS = 96;
const MIN_PARTIAL_REVOLVE_SEGMENTS = 24;
const MIN_LOOP_AREA = 1e-7;
const MIN_AXIS_DISTANCE = 1e-7;
const MIN_SIDE_TRIANGLE_COMPLETENESS = 0.98;

/**
 * 解析单条 profile 段的拓扑曲线类型。
 * @param segment profile 曲线段。
 * @returns 可写入拓扑快照的曲线类型。
 */
function resolveSegmentTopologyCurveKind(segment: ProfileCurveSegment): TopologyCurveKind {
    const segmentRecord = segment as unknown as Record<string, unknown>;
    switch (segment.kind) {
        case 'line':
            return 'line';
        case 'circle':
        case 'arc':
            return 'circle';
        case 'ellipse':
            return 'ellipse';
        case 'spline':
            return 'bspline';
        default:
            if (segmentRecord.kind === 'bezier') {
                return 'bezier';
            }
            if (segmentRecord.kind === 'bspline') {
                return 'bspline';
            }
            return 'other';
    }
}

/**
 * 合并闭合轮廓中多段曲线的拓扑类型。
 * @param curveKinds 轮廓内各段曲线类型。
 * @returns 轮廓级曲线类型。
 */
function resolveLoopTopologyCurveKindFromKinds(curveKinds: TopologyCurveKind[]): TopologyCurveKind {
    if (curveKinds.length <= 0) {
        return 'other';
    }
    const nonLineKinds = curveKinds.filter((curveKind) => curveKind !== 'line');
    if (nonLineKinds.length <= 0) {
        return 'line';
    }
    const firstCurveKind = nonLineKinds[0]!;
    return nonLineKinds.every((curveKind) => curveKind === firstCurveKind) ? firstCurveKind : 'other';
}

/**
 * 解析闭合轮廓的拓扑曲线类型。
 * @param segments profile 曲线段集合。
 * @returns 轮廓级曲线类型。
 */
function resolveLoopTopologyCurveKind(segments: ProfileCurveSegment[] | undefined): TopologyCurveKind {
    if (!Array.isArray(segments) || segments.length <= 0) {
        return 'other';
    }
    return resolveLoopTopologyCurveKindFromKinds(segments.map((segment) => resolveSegmentTopologyCurveKind(segment)));
}

/**
 * 判断输入值是否为普通对象。
 * @param value 待判断值。
 * @returns 是否为普通对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * 读取三维点。
 * @param value 原始值。
 * @returns 合法三维点，非法时返回 null。
 */
function readPoint3(value: unknown): Point3 | null {
    if (!Array.isArray(value) || value.length < 3) {
        return null;
    }
    const point = [Number(value[0]), Number(value[1]), Number(value[2])] as Point3;
    return point.every((item) => Number.isFinite(item)) ? point : null;
}

/**
 * 读取三维点列表。
 * @param value 原始值。
 * @returns 合法三维点列表。
 */
function readPoint3List(value: unknown): Point3[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value
        .map((item) => readPoint3(item))
        .filter((item): item is Point3 => item !== null);
}

/**
 * 计算向量点积。
 * @param left 左向量。
 * @param right 右向量。
 * @returns 点积。
 */
function dotPoint3(left: Point3, right: Point3): number {
    return left[0] * right[0] + left[1] * right[1] + left[2] * right[2];
}

/**
 * 计算向量叉积。
 * @param left 左向量。
 * @param right 右向量。
 * @returns 叉积向量。
 */
function crossPoint3(left: Point3, right: Point3): Point3 {
    return [
        left[1] * right[2] - left[2] * right[1],
        left[2] * right[0] - left[0] * right[2],
        left[0] * right[1] - left[1] * right[0],
    ];
}

/**
 * 计算两点距离。
 * @param left 左点。
 * @param right 右点。
 * @returns 距离。
 */
function distancePoint3(left: Point3, right: Point3): number {
    return Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2]);
}

/**
 * 归一化三维向量。
 * @param value 输入向量。
 * @param fallback 退化时使用的向量。
 * @returns 单位向量。
 */
function normalizePoint3(value: Point3, fallback: Point3): Point3 {
    const length = Math.hypot(value[0], value[1], value[2]);
    if (length <= POINT_EPSILON) {
        return [...fallback];
    }
    return [value[0] / length, value[1] / length, value[2] / length];
}

/**
 * 读取并归一化三维方向。
 * @param value 原始方向。
 * @param fallback 无效时的兜底方向。
 * @returns 单位方向。
 */
function readNormalizedDirection(value: unknown, fallback: Point3): Point3 {
    const point = readPoint3(value);
    return point ? normalizePoint3(point, fallback) : [...fallback];
}

/**
 * 判断两点是否重合。
 * @param left 左点。
 * @param right 右点。
 * @returns 是否重合。
 */
function arePointsCoincident(left: Point3, right: Point3): boolean {
    return distancePoint3(left, right) <= 1e-7;
}

/**
 * 移除闭合轮廓中重复的尾点。
 * @param points 原始点列。
 * @returns 不含重复尾点的点列。
 */
function normalizeClosedLoopPoints(points: Point3[]): Point3[] {
    if (points.length >= 2 && arePointsCoincident(points[0]!, points[points.length - 1]!)) {
        return points.slice(0, -1);
    }
    return [...points];
}

/**
 * 归一化特征归属 ID。
 * @param featureId 原始特征 ID。
 * @returns 可写入拓扑快照的特征 ID。
 */
function normalizeFeatureOwnerId(featureId: string | null | undefined): string | null {
    const normalized = typeof featureId === 'string' ? featureId.trim() : '';
    return normalized.length > 0 ? normalized : null;
}

/**
 * 计算二维向量叉积。
 * @param origin 公共起点。
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 叉积值。
 */
function crossVector2(origin: Vector2, left: Vector2, right: Vector2): number {
    return (left.x - origin.x) * (right.y - origin.y) - (left.y - origin.y) * (right.x - origin.x);
}

/**
 * 判断点是否落在线段包围范围内。
 * @param point 待判断点。
 * @param start 线段起点。
 * @param end 线段终点。
 * @returns 是否处于线段包围范围内。
 */
function isPointOnSegmentBounds(point: Vector2, start: Vector2, end: Vector2): boolean {
    return point.x >= Math.min(start.x, end.x) - POINT_EPSILON
        && point.x <= Math.max(start.x, end.x) + POINT_EPSILON
        && point.y >= Math.min(start.y, end.y) - POINT_EPSILON
        && point.y <= Math.max(start.y, end.y) + POINT_EPSILON;
}

/**
 * 判断两条二维线段是否相交。
 * @param startA 第一条线段起点。
 * @param endA 第一条线段终点。
 * @param startB 第二条线段起点。
 * @param endB 第二条线段终点。
 * @returns 是否相交。
 */
function doSegmentsIntersect(startA: Vector2, endA: Vector2, startB: Vector2, endB: Vector2): boolean {
    const crossA = crossVector2(startA, endA, startB);
    const crossB = crossVector2(startA, endA, endB);
    const crossC = crossVector2(startB, endB, startA);
    const crossD = crossVector2(startB, endB, endA);

    if (Math.abs(crossA) <= POINT_EPSILON && isPointOnSegmentBounds(startB, startA, endA)) {
        return true;
    }
    if (Math.abs(crossB) <= POINT_EPSILON && isPointOnSegmentBounds(endB, startA, endA)) {
        return true;
    }
    if (Math.abs(crossC) <= POINT_EPSILON && isPointOnSegmentBounds(startA, startB, endB)) {
        return true;
    }
    if (Math.abs(crossD) <= POINT_EPSILON && isPointOnSegmentBounds(endA, startB, endB)) {
        return true;
    }

    return (crossA > 0) !== (crossB > 0) && (crossC > 0) !== (crossD > 0);
}

/**
 * 计算闭合二维轮廓的有向面积。
 * @param contour 二维闭合轮廓。
 * @returns 有向面积。
 */
function computeSignedContourArea(contour: Vector2[]): number {
    let area = 0;
    contour.forEach((point, index) => {
        const nextPoint = contour[(index + 1) % contour.length]!;
        area += point.x * nextPoint.y - nextPoint.x * point.y;
    });
    return area / 2;
}

/**
 * 判断闭合二维轮廓是否存在自交。
 * @param contour 二维闭合轮廓。
 * @returns 是否自交。
 */
function hasSelfIntersection(contour: Vector2[]): boolean {
    for (let firstIndex = 0; firstIndex < contour.length; firstIndex += 1) {
        const firstNextIndex = (firstIndex + 1) % contour.length;
        const firstStart = contour[firstIndex]!;
        const firstEnd = contour[firstNextIndex]!;
        for (let secondIndex = firstIndex + 1; secondIndex < contour.length; secondIndex += 1) {
            const secondNextIndex = (secondIndex + 1) % contour.length;
            if (
                firstIndex === secondIndex
                || firstNextIndex === secondIndex
                || secondNextIndex === firstIndex
            ) {
                continue;
            }
            if (doSegmentsIntersect(firstStart, firstEnd, contour[secondIndex]!, contour[secondNextIndex]!)) {
                return true;
            }
        }
    }
    return false;
}

/**
 * 判断两个闭合二维轮廓边界是否相交。
 * @param left 左侧轮廓。
 * @param right 右侧轮廓。
 * @returns 是否存在边界相交。
 */
function doContoursIntersect(left: Vector2[], right: Vector2[]): boolean {
    for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
        const leftStart = left[leftIndex]!;
        const leftEnd = left[(leftIndex + 1) % left.length]!;
        for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
            if (doSegmentsIntersect(leftStart, leftEnd, right[rightIndex]!, right[(rightIndex + 1) % right.length]!)) {
                return true;
            }
        }
    }
    return false;
}

/**
 * 使用射线法判断点是否严格位于轮廓内部。
 * @param point 待判断点。
 * @param contour 二维闭合轮廓。
 * @returns 是否位于轮廓内部。
 */
function isPointInsideContour(point: Vector2, contour: Vector2[]): boolean {
    let inside = false;
    for (let index = 0, previousIndex = contour.length - 1; index < contour.length; previousIndex = index, index += 1) {
        const current = contour[index]!;
        const previous = contour[previousIndex]!;
        if (Math.abs(crossVector2(previous, current, point)) <= POINT_EPSILON
            && isPointOnSegmentBounds(point, previous, current)) {
            return false;
        }
        if (
            (current.y > point.y) !== (previous.y > point.y)
            && point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x
        ) {
            inside = !inside;
        }
    }
    return inside;
}

/**
 * 计算点到旋转轴的距离。
 * @param point 待判断点。
 * @param axisOrigin 旋转轴原点。
 * @param axisDirection 归一化旋转轴方向。
 * @returns 点到轴线的距离。
 */
function computePointAxisDistance(point: Point3, axisOrigin: Point3, axisDirection: Point3): number {
    const relative: Point3 = [
        point[0] - axisOrigin[0],
        point[1] - axisOrigin[1],
        point[2] - axisOrigin[2],
    ];
    const perpendicular = crossPoint3(relative, axisDirection);
    return Math.hypot(perpendicular[0], perpendicular[1], perpendicular[2]);
}

/**
 * 解析周期角度跨度。
 * @param startAngle 起始角。
 * @param endAngle 结束角。
 * @param clockwise 是否顺时针。
 * @returns 正向角度跨度。
 */
function resolveAngleSpan(startAngle: unknown, endAngle: unknown, clockwise: unknown): number {
    const start = Number(startAngle);
    const end = Number(endAngle);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return FULL_TURN_RADIANS;
    }
    let span = clockwise === true ? start - end : end - start;
    while (span <= 0) {
        span += FULL_TURN_RADIANS;
    }
    return Math.min(span, FULL_TURN_RADIANS);
}

/**
 * 判断角度是否等价于完整一周。
 * @param angle 输入角度。
 * @returns 是否为完整一周。
 */
function isFullTurn(angle: number): boolean {
    const normalized = Math.abs(angle) % FULL_TURN_RADIANS;
    return normalized <= 1e-6 || Math.abs(normalized - FULL_TURN_RADIANS) <= 1e-6;
}

/**
 * 将三维点追加到点列并去重。
 * @param points 目标点列。
 * @param point 待追加点。
 */
function appendPoint(points: Point3[], point: Point3): void {
    const last = points[points.length - 1];
    if (last && arePointsCoincident(last, point)) {
        return;
    }
    points.push(point);
}

/**
 * 按圆/圆弧参数采样。
 * @param segment 圆或圆弧曲线段。
 * @param segmentCount 采样段数。
 * @returns 采样点。
 */
function sampleCircularSegment(
    segment: Extract<ProfileCurveSegment, { kind: 'circle' | 'arc' }>,
    segmentCount: number,
): Point3[] {
    const normal = normalizePoint3(segment.normal, [0, 0, 1]);
    const xAxis = normalizePoint3(segment.xAxis, [1, 0, 0]);
    const yAxis = normalizePoint3(crossPoint3(normal, xAxis), [0, 1, 0]);
    const startAngle = segment.kind === 'arc' ? segment.startAngle : 0;
    const span = segment.kind === 'arc'
        ? resolveAngleSpan(segment.startAngle, segment.endAngle, segment.clockwise)
        : FULL_TURN_RADIANS;
    const points: Point3[] = [];

    for (let index = 0; index <= segmentCount; index += 1) {
        const ratio = index / segmentCount;
        const signedSpan = segment.kind === 'arc' && segment.clockwise ? -span : span;
        const angle = startAngle + signedSpan * ratio;
        const cosValue = Math.cos(angle);
        const sinValue = Math.sin(angle);
        points.push([
            segment.center[0] + (xAxis[0] * cosValue + yAxis[0] * sinValue) * segment.radius,
            segment.center[1] + (xAxis[1] * cosValue + yAxis[1] * sinValue) * segment.radius,
            segment.center[2] + (xAxis[2] * cosValue + yAxis[2] * sinValue) * segment.radius,
        ]);
    }

    return points;
}

/**
 * 按椭圆参数采样。
 * @param segment 椭圆曲线段。
 * @param segmentCount 完整椭圆的采样分段数。
 * @returns 采样点。
 */
function sampleEllipseSegment(
    segment: Extract<ProfileCurveSegment, { kind: 'ellipse' }>,
    segmentCount: number,
): Point3[] {
    const xAxis = normalizePoint3(segment.xAxis, [1, 0, 0]);
    const yAxis = normalizePoint3(segment.yAxis, [0, 1, 0]);
    const cosRotation = Math.cos(segment.rotation);
    const sinRotation = Math.sin(segment.rotation);
    const ellipseXAxis: Point3 = [
        cosRotation * xAxis[0] + sinRotation * yAxis[0],
        cosRotation * xAxis[1] + sinRotation * yAxis[1],
        cosRotation * xAxis[2] + sinRotation * yAxis[2],
    ];
    const ellipseYAxis: Point3 = [
        -sinRotation * xAxis[0] + cosRotation * yAxis[0],
        -sinRotation * xAxis[1] + cosRotation * yAxis[1],
        -sinRotation * xAxis[2] + cosRotation * yAxis[2],
    ];
    const points: Point3[] = [];

    for (let index = 0; index <= segmentCount; index += 1) {
        const angle = (FULL_TURN_RADIANS * index) / segmentCount;
        const cosValue = Math.cos(angle);
        const sinValue = Math.sin(angle);
        points.push([
            segment.center[0] + ellipseXAxis[0] * segment.radiusX * cosValue + ellipseYAxis[0] * segment.radiusY * sinValue,
            segment.center[1] + ellipseXAxis[1] * segment.radiusX * cosValue + ellipseYAxis[1] * segment.radiusY * sinValue,
            segment.center[2] + ellipseXAxis[2] * segment.radiusX * cosValue + ellipseYAxis[2] * segment.radiusY * sinValue,
        ]);
    }

    return points;
}

/**
 * 采样单条 profile 曲线段。
 * @param segment 曲线段。
 * @param curveSampleContext 曲线采样上下文。
 * @param profileSegmentCount 完整周期曲线的采样分段数。
 * @returns 采样点；无法安全采样时返回空数组。
 */
function sampleProfileCurveSegment(
    segment: ProfileCurveSegment,
    curveSampleContext: ProfileCurveSampleContext = {},
    profileSegmentCount = FINAL_PROFILE_CURVE_SEGMENTS,
): Point3[] {
    const segmentRecord = segment as unknown as Record<string, unknown>;
    switch (segment.kind) {
        case 'line':
            return [segment.start, segment.end];
        case 'circle':
            return sampleCircularSegment(segment, profileSegmentCount);
        case 'arc': {
            const span = resolveAngleSpan(segment.startAngle, segment.endAngle, segment.clockwise);
            const segmentCount = Math.max(8, Math.ceil((profileSegmentCount * span) / FULL_TURN_RADIANS));
            return sampleCircularSegment(segment, segmentCount);
        }
        case 'ellipse':
            return sampleEllipseSegment(segment, profileSegmentCount);
        case 'spline':
            return sampleProfileSplineCurveSegment(segment, curveSampleContext);
        default:
            if (segmentRecord.kind === 'bezier' || segmentRecord.kind === 'bspline') {
                const curvePoints = readPoint3List(segmentRecord.curvePoints);
                return curvePoints.length > 1
                    ? curvePoints
                    : readPoint3List(segmentRecord.controlPoints);
            }
            return [];
    }
}

/**
 * 按精确曲线段采样闭合轮廓。
 * @param segments 曲线段集合。
 * @param curveSampleContext 曲线采样上下文。
 * @param profileSegmentCount 完整周期曲线的采样分段数。
 * @returns 闭合轮廓点。
 */
function sampleLoopFromSegments(
    segments: ProfileCurveSegment[] | undefined,
    curveSampleContext: ProfileCurveSampleContext = {},
    profileSegmentCount = FINAL_PROFILE_CURVE_SEGMENTS,
): Point3[] | null {
    if (!Array.isArray(segments) || segments.length <= 0) {
        return null;
    }
    const points: Point3[] = [];
    for (const segment of segments) {
        const sampledPoints = sampleProfileCurveSegment(segment, curveSampleContext, profileSegmentCount);
        if (sampledPoints.length < 2) {
            return null;
        }
        sampledPoints.forEach((point) => appendPoint(points, point));
    }
    const loopPoints = normalizeClosedLoopPoints(points);
    return loopPoints.length >= 3 ? loopPoints : null;
}

/**
 * 从点列兜底采样闭合轮廓。
 * @param value 原始点列。
 * @returns 闭合轮廓点。
 */
function sampleLoopFromPoints(value: unknown): Point3[] {
    return normalizeClosedLoopPoints(readPoint3List(value));
}

/**
 * 从 ProfileRegionGeometry 中解析预览区域。
 * @param regionGeometry 区域几何。
 * @param curveSampleContext 曲线采样上下文。
 * @param profileSegmentCount 完整周期曲线的采样分段数。
 * @returns 预览区域；缺少有效轮廓时返回 null。
 */
function resolvePreviewRegion(
    regionGeometry: unknown,
    curveSampleContext: ProfileCurveSampleContext = {},
    profileSegmentCount = FINAL_PROFILE_CURVE_SEGMENTS,
): RevolvePreviewRegion | null {
    if (!isRecord(regionGeometry)) {
        return null;
    }

    const outerSegments = regionGeometry.outerSegments as ProfileCurveSegment[] | undefined;
    const outer = sampleLoopFromSegments(outerSegments, curveSampleContext, profileSegmentCount);
    const outerLoop = outer ?? (Array.isArray(outerSegments) ? [] : sampleLoopFromPoints(regionGeometry.outer));
    if (outerLoop.length < 3) {
        return null;
    }

    const loops: RevolvePreviewLoop[] = [{
        points: outerLoop,
        curveKind: resolveLoopTopologyCurveKind(outerSegments),
    }];
    const holes = Array.isArray(regionGeometry.holes) ? regionGeometry.holes : [];
    const holeSegments = Array.isArray(regionGeometry.holeSegments) ? regionGeometry.holeSegments : [];
    holes.forEach((hole, index) => {
        const segments = holeSegments[index] as ProfileCurveSegment[] | undefined;
        const sampled = sampleLoopFromSegments(segments, curveSampleContext, profileSegmentCount);
        const holeLoop = sampled ?? (Array.isArray(segments) ? [] : sampleLoopFromPoints(hole));
        if (holeLoop.length >= 3) {
            loops.push({
                points: holeLoop,
                curveKind: resolveLoopTopologyCurveKind(segments),
            });
        }
    });

    return {
        loops,
        normal: readNormalizedDirection(regionGeometry.normal, [0, 0, 1]),
        xAxis: readNormalizedDirection(regionGeometry.xAxis, [1, 0, 0]),
        yAxis: readNormalizedDirection(regionGeometry.yAxis, [0, 1, 0]),
    };
}

/**
 * 读取旋转预览区域列表。
 * @param params 旋转参数。
 * @param curveSampleContext 曲线采样上下文。
 * @param profileSegmentCount 完整周期曲线的采样分段数。
 * @returns 预览区域列表。
 */
function resolvePreviewRegions(
    params: RevolveParams,
    curveSampleContext: ProfileCurveSampleContext = {},
    profileSegmentCount = FINAL_PROFILE_CURVE_SEGMENTS,
): RevolvePreviewRegion[] {
    const geometries = Array.isArray(params.regionGeometries) && params.regionGeometries.length > 0
        ? params.regionGeometries
        : params.regionGeometry ? [params.regionGeometry] : [];
    return geometries
        .map((geometry) => resolvePreviewRegion(geometry, curveSampleContext, profileSegmentCount))
        .filter((region): region is RevolvePreviewRegion => region !== null);
}

/**
 * 围绕旋转轴旋转三维点。
 * @param point 原始点。
 * @param axisOrigin 轴上一点。
 * @param axisDirection 单位轴向。
 * @param angle 旋转角。
 * @returns 旋转后的点。
 */
function rotatePointAroundAxis(
    point: Point3,
    axisOrigin: Point3,
    axisDirection: Point3,
    angle: number,
): Point3 {
    const relative: Point3 = [
        point[0] - axisOrigin[0],
        point[1] - axisOrigin[1],
        point[2] - axisOrigin[2],
    ];
    const cosValue = Math.cos(angle);
    const sinValue = Math.sin(angle);
    const projectionLength = dotPoint3(relative, axisDirection);
    const parallel: Point3 = [
        axisDirection[0] * projectionLength,
        axisDirection[1] * projectionLength,
        axisDirection[2] * projectionLength,
    ];
    const perpendicular: Point3 = [
        relative[0] - parallel[0],
        relative[1] - parallel[1],
        relative[2] - parallel[2],
    ];
    const cross = crossPoint3(axisDirection, perpendicular);

    return [
        axisOrigin[0] + parallel[0] + perpendicular[0] * cosValue + cross[0] * sinValue,
        axisOrigin[1] + parallel[1] + perpendicular[1] * cosValue + cross[1] * sinValue,
        axisOrigin[2] + parallel[2] + perpendicular[2] * cosValue + cross[2] * sinValue,
    ];
}

/**
 * 追加单个三角形索引。
 * @param indices 索引数组。
 * @param positions 顶点坐标数组。
 * @param indexA 顶点 A。
 * @param indexB 顶点 B。
 * @param indexC 顶点 C。
 */
function appendTriangle(
    indices: number[],
    positions: number[],
    indexA: number,
    indexB: number,
    indexC: number,
): boolean {
    const ax = positions[indexA * 3]!;
    const ay = positions[indexA * 3 + 1]!;
    const az = positions[indexA * 3 + 2]!;
    const bx = positions[indexB * 3]!;
    const by = positions[indexB * 3 + 1]!;
    const bz = positions[indexB * 3 + 2]!;
    const cx = positions[indexC * 3]!;
    const cy = positions[indexC * 3 + 1]!;
    const cz = positions[indexC * 3 + 2]!;
    const edgeAB: Point3 = [bx - ax, by - ay, bz - az];
    const edgeAC: Point3 = [cx - ax, cy - ay, cz - az];
    const normal = crossPoint3(edgeAB, edgeAC);
    if (Math.hypot(normal[0], normal[1], normal[2]) <= POINT_EPSILON) {
        return false;
    }
    indices.push(indexA, indexB, indexC);
    return true;
}

/**
 * 旋转三维方向向量。
 * @param vector 原始方向。
 * @param axisDirection 单位旋转轴。
 * @param angle 旋转角度。
 * @returns 旋转后的方向。
 */
function rotateVectorAroundAxis(vector: Point3, axisDirection: Point3, angle: number): Point3 {
    const cosValue = Math.cos(angle);
    const sinValue = Math.sin(angle);
    const projectionLength = dotPoint3(vector, axisDirection);
    const parallel: Point3 = [
        axisDirection[0] * projectionLength,
        axisDirection[1] * projectionLength,
        axisDirection[2] * projectionLength,
    ];
    const perpendicular: Point3 = [
        vector[0] - parallel[0],
        vector[1] - parallel[1],
        vector[2] - parallel[2],
    ];
    const cross = crossPoint3(axisDirection, perpendicular);

    return normalizePoint3([
        parallel[0] + perpendicular[0] * cosValue + cross[0] * sinValue,
        parallel[1] + perpendicular[1] * cosValue + cross[1] * sinValue,
        parallel[2] + perpendicular[2] * cosValue + cross[2] * sinValue,
    ], vector);
}

/**
 * 把区域点投影到草图局部平面。
 * @param point 三维点。
 * @param origin 投影原点。
 * @param xAxis 局部 X 轴。
 * @param yAxis 局部 Y 轴。
 * @returns 二维投影点。
 */
function projectRegionPoint(point: Point3, origin: Point3, xAxis: Point3, yAxis: Point3): Vector2 {
    const relative: Point3 = [
        point[0] - origin[0],
        point[1] - origin[1],
        point[2] - origin[2],
    ];
    return new Vector2(
        dotPoint3(relative, xAxis),
        dotPoint3(relative, yAxis),
    );
}

/**
 * 判断二维轮廓点列是否可用于直接三角化。
 * @param contour 投影后的二维轮廓。
 * @returns 是否满足简单闭合区域要求。
 */
function isValidProjectedContour(contour: Vector2[]): boolean {
    if (contour.length < 3) {
        return false;
    }
    if (!contour.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) {
        return false;
    }
    if (Math.abs(computeSignedContourArea(contour)) <= MIN_LOOP_AREA) {
        return false;
    }
    return !hasSelfIntersection(contour);
}

/**
 * 将旋转区域中的闭合环投影到区域局部平面。
 * @param region 旋转区域。
 * @returns 投影后的轮廓集合；投影失败时返回 null。
 */
function projectRegionContours(region: RevolvePreviewRegion): Vector2[][] | null {
    const outerLoop = region.loops[0];
    if (!outerLoop) {
        return null;
    }
    const origin = outerLoop.points[0];
    if (!origin) {
        return null;
    }
    const contours = region.loops.map((loop) => (
        loop.points.map((point) => projectRegionPoint(point, origin, region.xAxis, region.yAxis))
    ));
    return contours.every((contour) => isValidProjectedContour(contour))
        ? contours
        : null;
}

/**
 * 校验孔轮廓是否是外轮廓内部的合法简单孔。
 * @param outerContour 外轮廓。
 * @param holeContour 孔轮廓。
 * @returns 是否是合法孔。
 */
function isValidHoleContour(outerContour: Vector2[], holeContour: Vector2[]): boolean {
    if (doContoursIntersect(outerContour, holeContour)) {
        return false;
    }
    return holeContour.every((point) => isPointInsideContour(point, outerContour));
}

/**
 * 判断旋转区域的闭合轮廓是否适合直接构建显示网格。
 * @param region 旋转区域。
 * @returns 是否可安全进入直接网格快速路径。
 */
function isValidRevolvePreviewRegion(region: RevolvePreviewRegion): boolean {
    const contours = projectRegionContours(region);
    if (!contours || contours.length <= 0) {
        return false;
    }
    const outerContour = contours[0]!;
    const holeContours = contours.slice(1);
    for (const holeContour of holeContours) {
        if (!isValidHoleContour(outerContour, holeContour)) {
            return false;
        }
    }
    for (let leftIndex = 0; leftIndex < holeContours.length; leftIndex += 1) {
        const leftContour = holeContours[leftIndex]!;
        for (let rightIndex = leftIndex + 1; rightIndex < holeContours.length; rightIndex += 1) {
            const rightContour = holeContours[rightIndex]!;
            if (
                doContoursIntersect(leftContour, rightContour)
                || isPointInsideContour(leftContour[0]!, rightContour)
                || isPointInsideContour(rightContour[0]!, leftContour)
            ) {
                return false;
            }
        }
    }
    return true;
}

/**
 * 判断区域轮廓是否贴近旋转轴导致直接网格退化。
 * @param region 旋转区域。
 * @param axisOrigin 旋转轴原点。
 * @param axisDirection 旋转轴方向。
 * @returns 是否存在轴退化风险。
 */
function hasAxisDegenerateLoopPoint(
    region: RevolvePreviewRegion,
    axisOrigin: Point3,
    axisDirection: Point3,
): boolean {
    return region.loops.some((loop) => (
        loop.points.some((point) => computePointAxisDistance(point, axisOrigin, axisDirection) <= MIN_AXIS_DISTANCE)
    ));
}

/**
 * 按目标法向追加三角形。
 * @param input 三角形追加输入。
 */
function appendOrientedTriangle(input: {
    indices: number[];
    positions: number[];
    indexA: number;
    indexB: number;
    indexC: number;
    targetNormal: Point3;
}): boolean {
    const ax = input.positions[input.indexA * 3]!;
    const ay = input.positions[input.indexA * 3 + 1]!;
    const az = input.positions[input.indexA * 3 + 2]!;
    const bx = input.positions[input.indexB * 3]!;
    const by = input.positions[input.indexB * 3 + 1]!;
    const bz = input.positions[input.indexB * 3 + 2]!;
    const cx = input.positions[input.indexC * 3]!;
    const cy = input.positions[input.indexC * 3 + 1]!;
    const cz = input.positions[input.indexC * 3 + 2]!;
    const normal = crossPoint3(
        [bx - ax, by - ay, bz - az],
        [cx - ax, cy - ay, cz - az],
    );
    if (Math.hypot(normal[0], normal[1], normal[2]) <= POINT_EPSILON) {
        return false;
    }
    if (dotPoint3(normal, input.targetNormal) >= 0) {
        input.indices.push(input.indexA, input.indexB, input.indexC);
        return true;
    }
    input.indices.push(input.indexA, input.indexC, input.indexB);
    return true;
}

/**
 * 根据三角形索引累积平滑法线。
 * @param positions 顶点坐标。
 * @param indices 三角形索引。
 * @returns 法线数组。
 */
function buildSmoothNormals(positions: number[], indices: number[]): number[] {
    const normals = new Array(positions.length).fill(0) as number[];
    for (let index = 0; index < indices.length; index += 3) {
        const indexA = indices[index]!;
        const indexB = indices[index + 1]!;
        const indexC = indices[index + 2]!;
        const ax = positions[indexA * 3]!;
        const ay = positions[indexA * 3 + 1]!;
        const az = positions[indexA * 3 + 2]!;
        const bx = positions[indexB * 3]!;
        const by = positions[indexB * 3 + 1]!;
        const bz = positions[indexB * 3 + 2]!;
        const cx = positions[indexC * 3]!;
        const cy = positions[indexC * 3 + 1]!;
        const cz = positions[indexC * 3 + 2]!;
        const normal = crossPoint3([bx - ax, by - ay, bz - az], [cx - ax, cy - ay, cz - az]);
        [indexA, indexB, indexC].forEach((vertexIndex) => {
            normals[vertexIndex * 3] += normal[0];
            normals[vertexIndex * 3 + 1] += normal[1];
            normals[vertexIndex * 3 + 2] += normal[2];
        });
    }

    for (let index = 0; index < normals.length; index += 3) {
        const normalized = normalizePoint3(
            [normals[index]!, normals[index + 1]!, normals[index + 2]!],
            [0, 0, 1],
        );
        normals[index] = normalized[0];
        normals[index + 1] = normalized[1];
        normals[index + 2] = normalized[2];
    }
    return normals;
}

/**
 * 构建单个闭合轮廓完整旋转后的侧面网格。
 * @param input 构建输入。
 */
function appendLoopRevolveSurface(input: {
    loop: RevolvePreviewLoop;
    axisOrigin: Point3;
    axisDirection: Point3;
    startAngle: number;
    sweepAngle: number;
    revolveSegments: number;
    positions: number[];
    indices: number[];
    faceTriangleRanges: [number, number][];
}): boolean {
    const points = input.loop.points;
    const startTriangleIndex = input.indices.length / 3;
    const vertexOffset = input.positions.length / 3;
    for (let revolveIndex = 0; revolveIndex <= input.revolveSegments; revolveIndex += 1) {
        const ratio = revolveIndex / input.revolveSegments;
        const angle = input.startAngle + input.sweepAngle * ratio;
        points.forEach((point) => {
            const rotated = rotatePointAroundAxis(point, input.axisOrigin, input.axisDirection, angle);
            input.positions.push(rotated[0], rotated[1], rotated[2]);
        });
    }

    const loopPointCount = points.length;
    const expectedTriangleCount = input.revolveSegments * loopPointCount * 2;
    let acceptedTriangleCount = 0;
    for (let revolveIndex = 0; revolveIndex < input.revolveSegments; revolveIndex += 1) {
        for (let pointIndex = 0; pointIndex < loopPointCount; pointIndex += 1) {
            const nextPointIndex = (pointIndex + 1) % loopPointCount;
            const currentRing = vertexOffset + revolveIndex * loopPointCount;
            const nextRing = vertexOffset + (revolveIndex + 1) * loopPointCount;
            const indexA = currentRing + pointIndex;
            const indexB = currentRing + nextPointIndex;
            const indexC = nextRing + nextPointIndex;
            const indexD = nextRing + pointIndex;
            if (appendTriangle(input.indices, input.positions, indexA, indexB, indexC)) {
                acceptedTriangleCount += 1;
            }
            if (appendTriangle(input.indices, input.positions, indexA, indexC, indexD)) {
                acceptedTriangleCount += 1;
            }
        }
    }
    const endTriangleIndex = input.indices.length / 3;
    if (endTriangleIndex > startTriangleIndex) {
        input.faceTriangleRanges.push([startTriangleIndex, endTriangleIndex]);
    }
    return expectedTriangleCount > 0
        && acceptedTriangleCount / expectedTriangleCount >= MIN_SIDE_TRIANGLE_COMPLETENESS;
}

/**
 * 追加非完整旋转的起止端面。
 * @param input 端面构建输入。
 */
function appendRegionCap(input: {
    region: RevolvePreviewRegion;
    axisOrigin: Point3;
    axisDirection: Point3;
    angle: number;
    normalSign: 1 | -1;
    positions: number[];
    indices: number[];
    faceTriangleRanges: [number, number][];
}): boolean {
    const outerLoop = input.region.loops[0];
    if (!outerLoop || outerLoop.points.length < 3) {
        return false;
    }
    const origin = outerLoop.points[0]!;
    const outerContour = outerLoop.points.map((point) => (
        projectRegionPoint(point, origin, input.region.xAxis, input.region.yAxis)
    ));
    const holeContours = input.region.loops.slice(1).map((loop) => (
        loop.points.map((point) => projectRegionPoint(point, origin, input.region.xAxis, input.region.yAxis))
    ));
    const triangles = ShapeUtils.triangulateShape(outerContour, holeContours);
    if (triangles.length === 0) {
        return false;
    }

    const startTriangleIndex = input.indices.length / 3;
    const vertexOffset = input.positions.length / 3;
    const capPoints = input.region.loops.flatMap((loop) => loop.points);
    capPoints.forEach((point) => {
        const rotated = rotatePointAroundAxis(point, input.axisOrigin, input.axisDirection, input.angle);
        input.positions.push(rotated[0], rotated[1], rotated[2]);
    });

    const rotatedNormal = rotateVectorAroundAxis(input.region.normal, input.axisDirection, input.angle);
    const targetNormal: Point3 = [
        rotatedNormal[0] * input.normalSign,
        rotatedNormal[1] * input.normalSign,
        rotatedNormal[2] * input.normalSign,
    ];
    let acceptedTriangleCount = 0;
    triangles.forEach((triangle) => {
        if (appendOrientedTriangle({
            indices: input.indices,
            positions: input.positions,
            indexA: vertexOffset + triangle[0]!,
            indexB: vertexOffset + triangle[1]!,
            indexC: vertexOffset + triangle[2]!,
            targetNormal,
        })) {
            acceptedTriangleCount += 1;
        }
    });

    const endTriangleIndex = input.indices.length / 3;
    if (endTriangleIndex > startTriangleIndex) {
        input.faceTriangleRanges.push([startTriangleIndex, endTriangleIndex]);
    }
    return acceptedTriangleCount === triangles.length;
}

/**
 * 根据旋转角度解析显示网格旋转分段数。
 * @param sweepAngle 旋转角。
 * @param fullTurnSegmentCount 完整旋转的分段数。
 * @returns 旋转分段数。
 */
function resolveRevolveSegmentCount(sweepAngle: number, fullTurnSegmentCount: number): number {
    if (isFullTurn(sweepAngle)) {
        return fullTurnSegmentCount;
    }
    const ratio = Math.min(1, Math.max(0, Math.abs(sweepAngle) / FULL_TURN_RADIANS));
    return Math.max(
        MIN_PARTIAL_REVOLVE_SEGMENTS,
        Math.ceil(fullTurnSegmentCount * ratio),
    );
}

/**
 * 从网格顶点数组中读取三维点。
 * @param positions 顶点坐标数组。
 * @param vertexIndex 顶点索引。
 * @returns 三维点。
 */
function readMeshPoint(positions: number[], vertexIndex: number): Point3 {
    const offset = vertexIndex * 3;
    return [
        positions[offset] ?? 0,
        positions[offset + 1] ?? 0,
        positions[offset + 2] ?? 0,
    ];
}

/**
 * 从三角形索引中读取三维点。
 * @param positions 顶点坐标数组。
 * @param indices 三角形索引数组。
 * @param triangleIndex 三角形索引。
 * @param vertexOffset 三角形内顶点偏移。
 * @returns 三维点。
 */
function readTrianglePoint(
    positions: number[],
    indices: number[],
    triangleIndex: number,
    vertexOffset: 0 | 1 | 2,
): Point3 {
    return readMeshPoint(positions, indices[triangleIndex * 3 + vertexOffset] ?? 0);
}

/**
 * 从面三角形范围中解析代表点与法向。
 * @param positions 顶点坐标数组。
 * @param indices 三角形索引数组。
 * @param triangleRange 面三角形范围。
 * @returns 面签名。
 */
function resolveFaceSignatureFromTriangleRange(
    positions: number[],
    indices: number[],
    triangleRange: [number, number],
): { point: Point3; normal: Point3 } {
    for (let triangleIndex = triangleRange[0]; triangleIndex < triangleRange[1]; triangleIndex += 1) {
        const pointA = readTrianglePoint(positions, indices, triangleIndex, 0);
        const pointB = readTrianglePoint(positions, indices, triangleIndex, 1);
        const pointC = readTrianglePoint(positions, indices, triangleIndex, 2);
        const normal = crossPoint3(
            [pointB[0] - pointA[0], pointB[1] - pointA[1], pointB[2] - pointA[2]],
            [pointC[0] - pointA[0], pointC[1] - pointA[1], pointC[2] - pointA[2]],
        );
        if (Math.hypot(normal[0], normal[1], normal[2]) > POINT_EPSILON) {
            return {
                point: [
                    (pointA[0] + pointB[0] + pointC[0]) / 3,
                    (pointA[1] + pointB[1] + pointC[1]) / 3,
                    (pointA[2] + pointB[2] + pointC[2]) / 3,
                ],
                normal: normalizePoint3(normal, [0, 0, 1]),
            };
        }
    }
    return {
        point: readMeshPoint(positions, 0),
        normal: [0, 0, 1],
    };
}

/**
 * 按旋转网格生成顺序解析每个面的曲面类型。
 * @param regions 旋转区域集合。
 * @param fullTurn 是否完整旋转一周。
 * @returns 与 faceTriangleRanges 对齐的曲面类型集合。
 */
function resolveCurveRevolveFaceSurfaceKinds(
    regions: RevolvePreviewRegion[],
    fullTurn: boolean,
): TopologySurfaceKind[] {
    const surfaceKinds: TopologySurfaceKind[] = [];
    regions.forEach((region) => {
        region.loops.forEach(() => {
            surfaceKinds.push('surface-of-revolution');
        });
        if (!fullTurn) {
            surfaceKinds.push('plane', 'plane');
        }
    });
    return surfaceKinds;
}

/**
 * 为直接曲线旋转网格创建拓扑面快照。
 * @param input 面构建输入。
 * @returns 拓扑面快照集合。
 */
function buildCurveRevolveTopologyFaces(input: {
    positions: number[];
    indices: number[];
    faceTriangleRanges: [number, number][];
    surfaceKinds: TopologySurfaceKind[];
    owningFeatureId: string | null;
}): TopologySnapshotFace[] {
    return input.faceTriangleRanges.map((triangleRange, faceIndex) => {
        const signature = resolveFaceSignatureFromTriangleRange(input.positions, input.indices, triangleRange);
        const ref = `curve-revolve:${buildTopologyFaceRef({
            point: signature.point,
            normal: signature.normal,
        })}`;
        return {
            index: faceIndex,
            subEntityId: buildTopologySubEntityId('face', ref),
            ref,
            owningFeatureId: input.owningFeatureId,
            adjacentEdgeIndices: [],
            adjacentFaceIndices: [],
            triangleRange,
            localNormal: signature.normal,
            localPoint: signature.point,
            surfaceKind: input.surfaceKinds[faceIndex] ?? 'other',
        };
    });
}

/**
 * 创建曲线参考顶点。
 * @param vertices 已有顶点集合。
 * @param point 顶点坐标。
 * @returns 新顶点索引。
 */
function appendCurveReferenceVertex(vertices: TopologySnapshotVertex[], point: Point3): number {
    const vertexIndex = vertices.length;
    const ref = `${buildTopologyVertexRef(point)}:${vertexIndex}`;
    vertices.push({
        index: vertexIndex,
        role: 'curve-reference',
        subEntityId: buildTopologySubEntityId('vertex', ref),
        ref,
        position: point,
    });
    return vertexIndex;
}

/**
 * 旋转整个轮廓点列。
 * @param points 原始轮廓点。
 * @param axisOrigin 旋转轴原点。
 * @param axisDirection 旋转轴方向。
 * @param angle 旋转角度。
 * @returns 旋转后的点列。
 */
function rotateLoopPoints(
    points: Point3[],
    axisOrigin: Point3,
    axisDirection: Point3,
    angle: number,
): Point3[] {
    return points.map((point) => rotatePointAroundAxis(point, axisOrigin, axisDirection, angle));
}

/**
 * 将闭合轮廓点列补齐为可画线的连续采样点。
 * @param points 未闭合或已闭合的轮廓点列。
 * @returns 首尾闭合的采样点列。
 */
function buildClosedSampledPoints(points: Point3[]): Point3[] {
    if (points.length <= 0) {
        return [];
    }
    const firstPoint = points[0]!;
    const lastPoint = points[points.length - 1]!;
    return arePointsCoincident(firstPoint, lastPoint)
        ? [...points]
        : [...points, [...firstPoint] as Point3];
}

/**
 * 解析闭合曲线边用于稳定引用的两个参考点。
 * @param sampledPoints 闭合曲线采样点。
 * @returns 曲线引用点。
 */
function resolveCurveEdgeReferencePoints(sampledPoints: Point3[]): { pointA: Point3; pointB: Point3 } | null {
    if (sampledPoints.length < 2) {
        return null;
    }
    const pointA = sampledPoints[0]!;
    const pointB = sampledPoints[Math.max(1, Math.floor((sampledPoints.length - 1) / 2))]!;
    if (arePointsCoincident(pointA, pointB)) {
        return null;
    }
    return { pointA, pointB };
}

/**
 * 写入一条直接曲线旋转网格的可视拓扑边。
 * @param input 边构建输入。
 */
function appendCurveRevolveTopologyEdge(input: {
    vertices: TopologySnapshotVertex[];
    edges: TopologySnapshotEdge[];
    faces: TopologySnapshotFace[];
    sampledPoints: Point3[];
    curveKind: TopologyCurveKind;
    edgeRole: string;
    adjacentFaceIndices: number[];
    owningFeatureId: string | null;
}): void {
    const referencePoints = resolveCurveEdgeReferencePoints(input.sampledPoints);
    if (!referencePoints) {
        return;
    }
    const adjacentFaces = input.adjacentFaceIndices
        .map((faceIndex) => input.faces[faceIndex])
        .filter((face): face is TopologySnapshotFace => Boolean(face));
    if (adjacentFaces.length <= 0) {
        return;
    }

    const edgeIndex = input.edges.length;
    const ref = `curve-revolve:${input.edgeRole}:${edgeIndex}:${buildTopologyEdgeRef(referencePoints.pointA, referencePoints.pointB)}`;
    const vertexAIndex = appendCurveReferenceVertex(input.vertices, referencePoints.pointA);
    const vertexBIndex = appendCurveReferenceVertex(input.vertices, referencePoints.pointB);
    input.edges.push({
        index: edgeIndex,
        subEntityId: buildTopologySubEntityId('edge', ref),
        ref,
        owningFeatureId: input.owningFeatureId,
        owningFaceRefs: adjacentFaces.map((face) => face.ref),
        adjacentFaceIndices: adjacentFaces.map((face) => face.index),
        edgeRole: input.edgeRole,
        curveKind: input.curveKind,
        vertexAIndex,
        vertexBIndex,
        sampledPoints: input.sampledPoints,
    });
    adjacentFaces.forEach((face) => {
        face.adjacentEdgeIndices.push(edgeIndex);
    });
}

/**
 * 为直接曲线旋转显示网格构建可视拓扑快照。
 * @param input 快照构建输入。
 * @returns 拓扑快照。
 */
function buildCurveRevolveVisualTopologySnapshot(input: {
    regions: RevolvePreviewRegion[];
    axisOrigin: Point3;
    axisDirection: Point3;
    startAngle: number;
    sweepAngle: number;
    positions: number[];
    indices: number[];
    faceTriangleRanges: [number, number][];
    owningFeatureId: string | null;
}): TopologySnapshot {
    const fullTurn = isFullTurn(input.sweepAngle);
    const faces = buildCurveRevolveTopologyFaces({
        positions: input.positions,
        indices: input.indices,
        faceTriangleRanges: input.faceTriangleRanges,
        surfaceKinds: resolveCurveRevolveFaceSurfaceKinds(input.regions, fullTurn),
        owningFeatureId: input.owningFeatureId,
    });
    const vertices: TopologySnapshotVertex[] = [];
    const edges: TopologySnapshotEdge[] = [];
    let faceCursor = 0;

    input.regions.forEach((region) => {
        const sideFaceIndices = region.loops.map(() => faceCursor++);
        const startCapFaceIndex = fullTurn ? -1 : faceCursor++;
        const endCapFaceIndex = fullTurn ? -1 : faceCursor++;

        region.loops.forEach((loop, loopIndex) => {
            const sideFaceIndex = sideFaceIndices[loopIndex] ?? -1;
            const startLoopPoints = buildClosedSampledPoints(rotateLoopPoints(
                loop.points,
                input.axisOrigin,
                input.axisDirection,
                input.startAngle,
            ));
            appendCurveRevolveTopologyEdge({
                vertices,
                edges,
                faces,
                sampledPoints: startLoopPoints,
                curveKind: loop.curveKind,
                edgeRole: fullTurn ? 'curve-profile-seam' : 'curve-profile-start',
                adjacentFaceIndices: fullTurn ? [sideFaceIndex] : [sideFaceIndex, startCapFaceIndex],
                owningFeatureId: input.owningFeatureId,
            });

            if (!fullTurn) {
                const endLoopPoints = buildClosedSampledPoints(rotateLoopPoints(
                    loop.points,
                    input.axisOrigin,
                    input.axisDirection,
                    input.startAngle + input.sweepAngle,
                ));
                appendCurveRevolveTopologyEdge({
                    vertices,
                    edges,
                    faces,
                    sampledPoints: endLoopPoints,
                    curveKind: loop.curveKind,
                    edgeRole: 'curve-profile-end',
                    adjacentFaceIndices: [sideFaceIndex, endCapFaceIndex],
                    owningFeatureId: input.owningFeatureId,
                });
            }
        });
    });

    return {
        vertices,
        faces,
        edges,
    };
}

/**
 * 判断是否可以使用曲线 profile 的轻量旋转预览网格。
 * @param params 旋转参数。
 * @param purpose 显示网格用途。
 * @returns 是否可以使用直接预览网格。
 */
function canUseCurveRevolveVisualMesh(
    params: RevolveParams,
    purpose: CurveRevolveVisualMeshOptions['purpose'],
): boolean {
    if (!hasCurveProfileParams(params)) {
        return false;
    }
    if (params.sourceType !== 'region_geometry') {
        return false;
    }
    const isCutOperation = params.featureKind === 'cut-revolve' || params.operation === 'cut';
    if (isCutOperation !== (purpose === 'cut-tool-preview')) {
        return false;
    }
    const direction1Angle = Number(params.direction1Angle);
    const direction2Angle = params.direction2Enabled === true ? Number(params.direction2Angle ?? 0) : 0;
    return Number.isFinite(direction1Angle)
        && Number.isFinite(direction2Angle)
        && Math.abs(direction1Angle + direction2Angle) > POINT_EPSILON;
}

/**
 * 构建曲线 profile 的旋转预览网格。
 *
 * 设计说明：
 * 1. 该网格只服务预览显示，不写入 GeometryState，也不替代正式 Puppy CAD 内核 特征结果；
 * 2. 完整 360 度旋转时，边界面可由 profile 采样点绕轴旋转直接得到，避免 BRepMesh 在两条曲率方向上做平方级细分；
 * 3. 如果输入不是安全的曲线闭合区域，返回 null 交给原 Puppy CAD 内核 预览链路兜底。
 *
 * @param params 旋转参数。
 * @param options 显示用途、特征归属与曲线采样上下文。
 * @returns 预览 MeshData，无法构建时返回 null。
 */
export function buildCurveRevolveVisualMesh(
    params: RevolveParams,
    options: CurveRevolveVisualMeshOptions = {},
): MeshData | null {
    const purpose = options.purpose ?? 'feature-result-preview';
    if (!canUseCurveRevolveVisualMesh(params, purpose)) {
        return null;
    }
    const meshDetail = options.meshDetail ?? 'final-result';
    const profileSegments = meshDetail === 'interactive-preview'
        ? INTERACTIVE_PROFILE_CURVE_SEGMENTS
        : FINAL_PROFILE_CURVE_SEGMENTS;
    const fullTurnRevolveSegments = meshDetail === 'interactive-preview'
        ? INTERACTIVE_REVOLVE_CURVE_SEGMENTS
        : FINAL_REVOLVE_CURVE_SEGMENTS;
    const regions = resolvePreviewRegions(params, options.curveSampleContext, profileSegments);
    if (regions.length <= 0 || !regions.every((region) => isValidRevolvePreviewRegion(region))) {
        return null;
    }
    const axisOrigin = readPoint3(params.axis?.point);
    const axisDirectionInput = readPoint3(params.axis?.direction);
    if (!axisOrigin || !axisDirectionInput) {
        return null;
    }
    const axisDirection = normalizePoint3(axisDirectionInput, [0, 0, 1]);
    if (regions.some((region) => hasAxisDegenerateLoopPoint(region, axisOrigin, axisDirection))) {
        return null;
    }
    const direction2Angle = params.direction2Enabled === true ? (params.direction2Angle ?? 0) : 0;
    const startAngle = -direction2Angle;
    const sweepAngle = params.direction1Angle + direction2Angle;
    const revolveSegments = resolveRevolveSegmentCount(sweepAngle, fullTurnRevolveSegments);
    const sweepSign = sweepAngle >= 0 ? 1 : -1;
    const fullTurn = isFullTurn(sweepAngle);
    const positions: number[] = [];
    const indices: number[] = [];
    const faceTriangleRanges: [number, number][] = [];
    let expectedFaceRangeCount = 0;
    let isMeshComplete = true;

    regions.forEach((region) => {
        region.loops.forEach((loop) => {
            expectedFaceRangeCount += 1;
            isMeshComplete = appendLoopRevolveSurface({
                loop,
                axisOrigin,
                axisDirection,
                startAngle,
                sweepAngle,
                revolveSegments,
                positions,
                indices,
                faceTriangleRanges,
            }) && isMeshComplete;
        });
        if (!fullTurn) {
            expectedFaceRangeCount += 2;
            isMeshComplete = appendRegionCap({
                region,
                axisOrigin,
                axisDirection,
                angle: startAngle,
                normalSign: sweepSign === 1 ? -1 : 1,
                positions,
                indices,
                faceTriangleRanges,
            }) && isMeshComplete;
            isMeshComplete = appendRegionCap({
                region,
                axisOrigin,
                axisDirection,
                angle: startAngle + sweepAngle,
                normalSign: sweepSign === 1 ? 1 : -1,
                positions,
                indices,
                faceTriangleRanges,
            }) && isMeshComplete;
        }
    });

    if (
        !isMeshComplete
        || positions.length <= 0
        || indices.length <= 0
        || faceTriangleRanges.length !== expectedFaceRangeCount
    ) {
        return null;
    }

    const owningFeatureId = normalizeFeatureOwnerId(options.featureId);
    const topologySnapshot = buildCurveRevolveVisualTopologySnapshot({
        regions,
        axisOrigin,
        axisDirection,
        startAngle,
        sweepAngle,
        positions,
        indices,
        faceTriangleRanges,
        owningFeatureId,
    });

    return {
        id: 'revolve-preview',
        positions: new Float32Array(positions),
        normals: new Float32Array(buildSmoothNormals(positions, indices)),
        indices: new Uint32Array(indices),
        faceTriangleRanges,
        topologySnapshot,
        metadata: {
            visualMeshStrategy: 'curve-revolve-direct-mesh',
            visualMeshPurpose: purpose,
            visualMeshDetail: meshDetail,
            topologySnapshotSource: 'curve-revolve-visual-mesh',
            revolveSegments,
            profileSegments,
        },
    };
}
